import { useState, useEffect, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import B2BOrdersTable from './B2BOrdersTable';
import AddB2BOrderModal from './AddB2BOrderModal';
import ViewB2BOrderModal from './ViewB2BOrderModal';
import DeleteB2BOrderModal from './DeleteB2BOrderModal';
import EditOrderStatusModal from './EditOrderStatusModal';
import PDFViewerModal from './PDFViewerModal';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { B2BOrderService } from '../../../services/B2BServices';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { hasStoredPermission } from '../../../utils/dashboardPermissions';

const B2BOrders = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isInvoiceUnavailableOpen, setIsInvoiceUnavailableOpen] = useState(false);
  const [invoiceUnavailableMessage, setInvoiceUnavailableMessage] = useState('');
  const [retryingInvoice, setRetryingInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  
  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const fetchOrders = async (
    page = 1,
    pageSize = 10,
    search = '',
    currentSortModel = sortModel,
    currentFilterModel = filterModel
  ) => {
    console.log('🔍 B2BOrders - fetchOrders called with:', { page, pageSize, search });
    try {
      setLoading(true);
      
      // Verificar si hay clientData en el state de la navegación o en los query params
      const clientData = location.state?.clientData;
      const clientIdFromParams = searchParams.get('clientId');
      const clientId = clientData?.clientId || clientIdFromParams;
      const destinationLocationId = searchParams.get('destinationLocationId');
      
      console.log('🔍 B2BOrders - clientData:', clientData);
      console.log('🔍 B2BOrders - clientIdFromParams:', clientIdFromParams);
      console.log('🔍 B2BOrders - final clientId:', clientId);
      
      let response;
      
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const requestOptions = {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
        destinationLocationId
      };

      if (clientId) {
        // Si hay clientId, hacer fetch específico del cliente
        console.log('🔍 B2BOrders - Fetching orders for client:', clientId);
        response = await B2BOrderService.getOrdersByClient(clientId, page, pageSize, search, requestOptions);
      } else {
        // Si no hay clientId, hacer fetch general
        console.log('🔍 B2BOrders - Fetching all orders');
        response = await B2BOrderService.getOrders(page, pageSize, search, requestOptions);
      }
      
      console.log('🔍 B2BOrders - Response received:', response);
      
      // Manejar la nueva estructura de respuesta
      if (response && typeof response === 'object') {
        setOrders(response.orders || []);
        setPagination({
          totalCount: response.totalCount || 0,
          currentPage: response.currentPage || page,
          totalPages: response.totalPages || 1,
          pageSize: pageSize
        });
      } else {
        // Fallback para respuestas antiguas
        setOrders(Array.isArray(response) ? response : []);
        setPagination(prev => ({ ...prev, totalCount: Array.isArray(response) ? response.length : 0 }));
      }
    } catch (error) {
      console.error('Error fetching B2B orders:', error);
      // En caso de error, mostrar array vacío
      setOrders([]);
      setPagination(prev => ({ ...prev, totalCount: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    const newPage = newModel.page + 1; // DataGrid usa índice base 0, nuestro backend usa base 1
    fetchOrders(newPage, newModel.pageSize, searchTerm, sortModel, filterModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    fetchOrders(1, pagination.pageSize, searchTerm, newSortModel, filterModel);
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    const normalizedModel = { ...newFilterModel, items: normalizedItems };
    setFilterModel(normalizedModel);
    fetchOrders(1, pagination.pageSize, searchTerm, sortModel, normalizedModel);
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchOrders(1, pagination.pageSize, newSearchTerm, sortModel, filterModel);
    }, 500);
  };

  useEffect(() => {
    console.log('🔍 B2BOrders - useEffect triggered');
    console.log('🔍 B2BOrders - searchParams:', searchParams.toString());
    console.log('🔍 B2BOrders - location.state:', location.state);
    fetchOrders(1, pagination.pageSize, searchTerm, sortModel, filterModel);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchParams, location.state]);

  const handleAddOrder = async (newOrder) => {
    try {
      // Usar el servicio real para crear pedido
      const createdOrder = await B2BOrderService.createOrder(newOrder);
      // Refetch data after adding
      fetchOrders(pagination.currentPage, pagination.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error creating B2B order:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };

  const handleDeleteOrder = async () => {
    try {
      // Usar el servicio real para eliminar pedido
      await B2BOrderService.deleteOrder(selectedOrder.id);
      // Refetch data after deleting
      fetchOrders(pagination.currentPage, pagination.pageSize, searchTerm, sortModel, filterModel);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting B2B order:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };  

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsViewModalOpen(true);
  };

  const handleViewInvoice = async (order) => {
    try {
      console.log('Generating PDF for order:', order);

      // Activar loading para este pedido específico
      setLoadingOrderId(order.id);

      console.log('Using orderId for invoice PDF:', order.id);
      const pdfBlob = await B2BOrderService.generateOrderPDF(order.id);
      console.log('PDF blob received:', pdfBlob);

      // Crear URL del blob para mostrar en el visor
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      console.log('PDF URL created:', pdfUrl);

      // Establecer la URL y abrir el modal
      setPdfUrl(pdfUrl);
      setSelectedOrder(order);
      setIsPDFModalOpen(true);

    } catch (error) {
      console.error('Error generating order PDF:', error);
      // El backend distingue "todavía no hay factura" de un error genérico y manda
      // el motivo real (ver B2BOrderService.getOrderInvoicePdf en axium-backend);
      // mostramos ese caso en un diálogo con opción de reintentar en vez de un
      // toast genérico, para no dejar al usuario sin saber qué pasó.
      if (error.isInvoiceNotAvailable) {
        setSelectedOrder(order);
        setInvoiceUnavailableMessage(error.message);
        setIsInvoiceUnavailableOpen(true);
      } else {
        SnackbarUtils.error(t('error_generating_pdf'));
      }
    } finally {
      // Desactivar loading para este pedido específico
      setLoadingOrderId(null);
    }
  };

  const handleCloseInvoiceUnavailableModal = () => {
    setIsInvoiceUnavailableOpen(false);
    setInvoiceUnavailableMessage('');
  };

  const handleRetryInvoiceEmission = async () => {
    if (!selectedOrder) return;
    try {
      setRetryingInvoice(true);
      await B2BOrderService.retryInvoiceEmission(selectedOrder.id);
      SnackbarUtils.success(t('invoice_emission_retry_success'));
      setIsInvoiceUnavailableOpen(false);
      await handleViewInvoice(selectedOrder);
    } catch (error) {
      const message = error?.response?.data?.error_message || t('invoice_emission_retry_failed');
      SnackbarUtils.error(message);
    } finally {
      setRetryingInvoice(false);
    }
  };

  const handleEditStatus = (order) => {
    setSelectedOrder(order);
    setIsEditStatusModalOpen(true);
  };

  const handleUpdateOrderStatus = async (statusData) => {
    try {
      const updatedOrder = await B2BOrderService.updateOrderStatus(selectedOrder.id, statusData);
      console.log('B2BOrders - Updated order status:', updatedOrder);
      
      // Refetch data after updating
      fetchOrders(pagination.currentPage, pagination.pageSize, searchTerm, sortModel, filterModel);
      
      setIsEditStatusModalOpen(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handleEditPaymentStatus = async (order, paymentStatusData) => {
    try {
      // Usar el servicio real para actualizar estado de pago
      const response = await B2BOrderService.updatePaymentStatus(order.id, paymentStatusData);
      console.log('B2BOrders - Updated order from backend:', response);
      
      // Refetch data after updating
      fetchOrders(pagination.currentPage, pagination.pageSize, searchTerm);
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
      }
  };

  const handleClosePDFModal = () => {
    setIsPDFModalOpen(false);
    setLoadingOrderId(null);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Obtener información del cliente si está disponible
  const clientData = location.state?.clientData;
  const clientIdFromParams = searchParams.get('clientId');
  const destinationLocationId = searchParams.get('destinationLocationId');
  const destinationLocationName = searchParams.get('destinationLocationName');
  const clientId = clientData?.clientId || clientIdFromParams;
  const isDestinationScopedView = Boolean(destinationLocationId);
  const canCreateB2BOrders = hasStoredPermission('CREATE_B2B_ORDERS');
  
  const pageTitle = clientData?.clientName 
    ? `${t('b2b_orders')} - ${clientData.clientName}`
    : clientIdFromParams 
      ? `${t('b2b_orders')} - Cliente ${clientIdFromParams}`
      : destinationLocationId
        ? `${t('b2b_orders')} - ${destinationLocationName || `Local ${destinationLocationId}`}`
      : t('b2b_orders');
  const pageSubtitle = clientData?.clientName 
    ? `${t('manage_b2b_orders')} - ${clientData.clientName}`
    : clientIdFromParams 
      ? `${t('manage_b2b_orders')} - Cliente ${clientIdFromParams}`
      : destinationLocationId
        ? 'Pedidos asociados a esta franquicia'
      : t('manage_b2b_orders');

  return (
    <Box p={3}>
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />
      <B2BOrdersTable
        loading={loading}
        loadingOrderId={loadingOrderId}
        orders={orders}
        pagination={pagination}
        searchTerm={searchTerm}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        onRefresh={() => fetchOrders(pagination.currentPage, pagination.pageSize, searchTerm, sortModel, filterModel)}
        onAdd={() => setIsAddModalOpen(true)}
        onView={handleViewOrder}
        onViewInvoice={handleViewInvoice}
        onEditStatus={handleEditStatus}
        onDelete={(order) => {
          setSelectedOrder(order);
          setIsDeleteModalOpen(true);
        }}
        onEditPaymentStatus={handleEditPaymentStatus}
        onPaginationChange={handlePaginationChange}
        onSearchChange={handleSearchChange}
        showAddButton={canCreateB2BOrders}
        readOnlyActions={isDestinationScopedView}
      />
      <AddB2BOrderModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddOrder}
        fixedDestinationLocationId={isDestinationScopedView ? destinationLocationId : null}
        fixedDestinationLocationName={isDestinationScopedView ? destinationLocationName : ''}
      />
      <ViewB2BOrderModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        order={selectedOrder}
      />
      <DeleteB2BOrderModal
        orderNumber={selectedOrder?.orderNumber}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteOrder}
      />
      <EditOrderStatusModal
        open={isEditStatusModalOpen}
        onClose={() => setIsEditStatusModalOpen(false)}
        onSave={handleUpdateOrderStatus}
        order={selectedOrder}
      />
      <PDFViewerModal
        open={isPDFModalOpen}
        onClose={handleClosePDFModal}
        order={selectedOrder}
        pdfUrl={pdfUrl}
      />
      <Dialog open={isInvoiceUnavailableOpen} onClose={handleCloseInvoiceUnavailableModal} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoice_not_available_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{invoiceUnavailableMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoiceUnavailableModal}>{t('close')}</Button>
          <Button variant="contained" onClick={handleRetryInvoiceEmission} disabled={retryingInvoice}>
            {retryingInvoice ? t('retrying_invoice_emission') : t('retry_invoice_emission')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default B2BOrders;
